import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { PatientActionMenu } from '@/apps/latest/components/molecules/PatientActionMenu'
import { RemoteGuidanceRow } from '@/apps/latest/components/molecules/RemoteGuidanceRow'
import { PatientInfoBar } from '@/apps/latest/components/molecules/PatientInfoBar'
import { GuidancePrepPane } from '@/apps/latest/components/organisms/GuidancePrepPane'
import { MeetingBlock } from '@/apps/latest/components/organisms/MeetingBlock'
import { PostRecordingView } from '@/apps/latest/components/organisms/PostRecordingView'
import { useSidebarYakurekiList } from '@/apps/latest/components/organisms/Sidebar/useSidebarYakurekiList'
import type { MemoFlush } from '@/apps/latest/features/yakureki/components/MemoPanel'
import { Layout } from '@/apps/latest/components/templates/Layout'
import { useActiveRecordingEmit } from '@/apps/latest/shared/recording-status/useActiveRecordingEmit'
import { useGoneRedirect } from '@/apps/latest/shared/recording-status/useGoneRedirect'
import { usePollEvent } from '@/apps/latest/shared/recording-status/usePollEvent'
import { useRecordingStatus } from '@/apps/latest/shared/recording-status/useRecordingStatus'
import { useSessionPharmacist, usePharmacistsQuery } from '@/features/auth'
import { blocksOwnRecording, isBeforeGuidance } from '@/features/remoteGuidance'
import {
  useGenerateFromMemoMutation,
  useManualSoapMutation,
  usePatchYakurekiEventMutation,
  useSessionTypeMutation,
} from '@/features/yakureki'
import {
  DEFAULT_RECORDING_PRESET,
  type RecordingPreset,
} from '@/features/yakureki/recording/streaming/recordingPreset'
import {
  PatientMismatchBanner,
  useAnyCompassEmbed,
  useSyncPatientBackground,
} from '@/features/anycompass'
import { useToast } from '@/shared/contexts/ToastContext'
import { HomecareVisitBackLink } from '@/apps/latest/features/home-visit/HomecareVisitBackLink'
import { isFailureStatus, type SessionType } from '@/shared/types/yakurekiCore'

// log/33 P5 で Pre/Post 二画面切替に拡張. 単一 URL (/soap/:requestId) で event.status を見て
// Pre (録音前/中/処理中) / PostRecordingView (完了/失敗) を state-based switch.
// log/33/work-log/36 で ViewStatus 3-layer 設計に再整備 (= 案 B).
//
// 在宅の訪問のページ (/homecare/visits/:requestId) も同じ器で描く (homecare)。録音・SOAP・
// 訪問報告書は外来と同じ部品のままで、違うのはパンくずと頭の「← 患者のページ」だけ。
interface IndexPageProps {
  homecare?: boolean
}

const IndexPage: FC<IndexPageProps> = ({ homecare = false }) => {
  const { requestId } = useParams<{ requestId: string }>()
  const yakurekiId = requestId === 'new' || !requestId ? null : requestId
  // AnyCOMPASS 埋め込み時は PatientInfoBar 下の余白を 1 段詰め、記録カードの上端を
  // ホスト左ペインの「今回処方」バンドの上端に揃える (PostRecordingView の mb:2 とも一致)。
  const { isEmbedded } = useAnyCompassEmbed()
  // ホスト薬歴の頭書きを back へ預ける (SOAP 生成の `# 患者背景` の材料).
  // 薬歴が確定した時点で 1 回だけ. 非埋め込み / 頭書き未受領なら何もしない.
  useSyncPatientBackground(yakurekiId)

  // L1 (server canonical event). polling cadence の知識は usePollEvent が単独で持つ
  // (processing 中の追従 + 在宅サイドカーの猶予). ここで判定を書き足さない.
  const { data: event, isGone } = usePollEvent({ yakurekiId })

  // 録音 caller の context (= 担当薬剤師 / メモ / 家族同伴) を組み立てる helper 群.
  const { pharmacists } = usePharmacistsQuery()
  const pharmacistList = useMemo(
    () => (pharmacists?.pharmacists ?? []).filter((p) => p.is_own),
    [pharmacists]
  )
  const { sessionPharmacistId, setSessionPharmacistId } =
    useSessionPharmacist(pharmacistList)
  const patchEventMutation = usePatchYakurekiEventMutation()

  // 録音開始時点の context を snapshot として ref に握る. fallback 経路 commit / retry 時に
  // start 時点の値を参照する (= event は polling で更新されうるため).
  const startSnapshotRef = useRef<{
    pharmacistId?: string
    memoItems: string[]
    label: string
    hasCompanion: boolean
  }>({ pharmacistId: undefined, memoItems: [], label: '', hasCompanion: false })

  // fallback 経路で stop された時に hook から呼ばれる. legacy batch endpoint に流すことで
  // back の SOAP pipeline を起動.
  const onFallbackCommit = useCallback(
    async (blob: Blob): Promise<void> => {
      if (!yakurekiId) throw new Error('yakurekiId missing for fallback commit')
      const file = new File([blob], 'audio.mp4', {
        type: blob.type || 'audio/mp4',
      })
      const snap = startSnapshotRef.current
      await patchEventMutation.mutateAsync({
        audioSource: file,
        label: snap.label,
        yakurekiId,
        pharmacistId: snap.pharmacistId,
        memoItems: snap.memoItems,
        // 同伴/代理の別: batch 経路は stream/start を経ないため commit で運ぶ (log/207)。
        // 劣化 fallback では start 時に送信済みの同値の再送 = 冪等。
        hasCompanionOrSubstitute: snap.hasCompanion,
      })
    },
    [yakurekiId, patchEventMutation]
  )

  // 遷移で運ばれた「意図した batch」 hint の raw 値. dwell の経路分岐用に hook へ注入する
  // (invalidation は hook 内が担う). 表示用の routedIntendedBatch (下方) は
  // recordingStatus に依存する invalidation 込みのため hook 呼び出し前に使えず、 別に読む.
  const location = useLocation()
  const rawIntendedBatchHint =
    (location.state as { intendedBatch?: boolean } | null)?.intendedBatch ===
    true
  // 別 page (新規録音 / まとめて録音) の録音完了から遷移してきた証拠 (ADR-0041).
  // この page の engine は idle で cache はまだ 'recording' のことがあり、 証拠が無いと
  // derive は「録音していない × サーバは録音中」を idle (mic) と読む.
  const rawFinalizedHint =
    (location.state as { finalized?: boolean } | null)?.finalized === true

  const recordingStatus = useRecordingStatus({
    event,
    onFallbackCommit,
    intendedBatchHint: rawIntendedBatchHint,
    finalizedHint: rawFinalizedHint,
  })
  // method 呼び出しの receiver を deps に含めず callback を安定させるため destructure する.
  const { startRecording } = recordingStatus
  useActiveRecordingEmit(recordingStatus)

  // 同伴/代理人有 の user override. undefined = back の event.has_companion_or_substitute を採用.
  // popover で toggle されたら override が立ち、 以降は back 値より優先.
  const [hasCompanionOverride, setHasCompanionOverride] = useState<
    boolean | undefined
  >(undefined)
  const hasCompanion =
    hasCompanionOverride ?? event?.has_companion_or_substitute ?? false

  // 録音モード (標準/深掘り). ⫻ 録音設定 popover で選ぶ. intent を back に送るだけ.
  const [preset, setPreset] = useState<RecordingPreset>(
    DEFAULT_RECORDING_PRESET
  )

  // 来局/在宅 (session_type). preset と違い back に永続化される属性なので pill 変更で即 PATCH し、
  // 表示は event.session_type を source of truth とする (back は PENDING の間のみ変更を許可).
  const sessionTypeMutation = useSessionTypeMutation()
  const handleSessionTypeChange = useCallback(
    (next: SessionType) => {
      if (!yakurekiId) return
      sessionTypeMutation.mutate({ yakurekiId, sessionType: next })
    },
    [yakurekiId, sessionTypeMutation]
  )

  // 録音せずに SOAP へ進む (Zendesk #2237). 露出は AnyCOMPASS 埋め込み中かつ録音前
  // (PENDING) のみ. back に channel ガードは無く、出し分けはここだけで閉じる
  // (= 連携ビルド以外では isEmbedded が構造的に false).
  //
  // メモがあれば「メモから生成」、無ければ「空 SOAP を起こすだけ」に着地する.
  // 導線は 1 本のまま label で結果を宣言する (= affordance を増やさない).
  //   メモあり: 非同期生成 → status='processing' → polling が 'completed' を拾う
  //   メモなし: 同期で空 SOAP → status='completed' → 即 Post 画面
  //
  // **どちらを呼ぶかを client の event.memo_items で判断してはいけない**.
  // メモは MemoPanel が debounce(1500ms)/blur で保存するので、書いた直後に押すと
  // event.memo_items がまだ空で、空 SOAP を作って completed にしてしまう
  // (以降 generate-from-memo は 409 = 取り返しがつかない).
  // 手順は 2 段構え:
  //   1. 押下時に memo を flush して着地を待つ (= back が最新を読める状態にする)
  //   2. 常に generate-from-memo を叩き、「メモが無い」(400) と言われたときだけ空 SOAP に倒す
  //      = 判断は back の guard 1 箇所に寄せる
  const manualSoapMutation = useManualSoapMutation()
  const generateFromMemoMutation = useGenerateFromMemoMutation()
  const { showToast } = useToast()
  const memoFlushRef = useRef<MemoFlush | null>(null)
  const [isSkipPending, setIsSkipPending] = useState(false)
  const canSkipRecording =
    isEmbedded && !!yakurekiId && event?.status === 'pending'
  // label 用のヒント. 実際の分岐には使わない (上記のとおり stale になりうるため).
  // 空白のみのメモは根拠にならない (back の guard と同じ判定).
  const hasMemo = (event?.memo_items ?? []).some((m) => m.trim() !== '')

  // 遠隔に依頼中 (頼む店・生きている・指導済の前) の処方は、この店で記録しない (マイク・
  // 録音せずに記載。まとめて録音は Sidebar の選択と recording-group で同じ線)。
  // 遠隔の店が指導を終えると通話の音声が処方に入って SOAP になるので、ここで記録すると
  // 片方がもう片方を上書きする。札は一覧 (2 秒ごとの差分) の方が新しい
  const { data: sidebarList } = useSidebarYakurekiList()
  const remoteInProgress = useMemo(() => {
    const remote =
      sidebarList?.find((y) => y.id === yakurekiId)?.remote ?? event?.remote
    return blocksOwnRecording(remote) ? remote : null
  }, [sidebarList, yakurekiId, event?.remote])
  // 止めたら true。指導中は取り消せないので、取り消しを案内しない (back log/250 決定 27・31)
  const rejectIfRemoteInProgress = useCallback(() => {
    if (!remoteInProgress) return false
    showToast(
      isBeforeGuidance(remoteInProgress)
        ? '遠隔に依頼中の処方です。この店で記録するには、先に依頼を取り消してください'
        : '遠隔の店で指導中の処方です。指導が終わるまで、この店では記録できません',
      'error'
    )
    return true
  }, [remoteInProgress, showToast])

  const runSkipRecording = useCallback(async () => {
    if (!yakurekiId) return
    if (rejectIfRemoteInProgress()) return
    setIsSkipPending(true)
    try {
      try {
        await memoFlushRef.current?.()
      } catch {
        // 保存に失敗しても生成は試みる (メモが無いと判定されれば下で空 SOAP に倒れる).
      }
      try {
        await generateFromMemoMutation.mutateAsync({ yakurekiId })
      } catch (e) {
        // この endpoint の 400 は「メモが空」だけ (それ以外の前提崩れは 409/403).
        const status = (e as { response?: { status?: number } })?.response
          ?.status
        if (status !== 400) {
          showToast('メモからの生成を開始できませんでした', 'error')
          return
        }
        await manualSoapMutation.mutateAsync({ yakurekiId }).catch(() => {
          // back の 409 (既に SOAP がある / 録音済) を含め、押しても無反応にはしない.
          showToast('記載を開始できませんでした', 'error')
        })
      }
    } finally {
      setIsSkipPending(false)
    }
  }, [
    yakurekiId,
    rejectIfRemoteInProgress,
    generateFromMemoMutation,
    manualSoapMutation,
    showToast,
  ])

  const handleSkipRecording = useCallback(() => {
    void runSkipRecording()
  }, [runSkipRecording])

  const handleStartRecording = useCallback(() => {
    if (!yakurekiId) return
    if (rejectIfRemoteInProgress()) return
    const memoItems = event?.memo_items ?? []
    const pharmacistId = sessionPharmacistId || undefined
    const label = event?.label ?? ''
    startSnapshotRef.current = { pharmacistId, memoItems, label, hasCompanion }
    startRecording({
      yakurekiIds: [yakurekiId],
      pharmacistId,
      memoItems,
      hasCompanionOrSubstituteMap: hasCompanion
        ? { [yakurekiId]: true }
        : undefined,
      preset,
    })
  }, [
    yakurekiId,
    rejectIfRemoteInProgress,
    startRecording,
    event,
    sessionPharmacistId,
    hasCompanion,
    preset,
  ])

  // engine の terminal cleanup. completed / failed (= pendingBlob なし) で engine を idle に倒す.
  // viewStatus は L3 の ratchet で 'completed' / 'failed' のまま保持されるため、 engine reset は
  // 安全 (= UI 表示の連続性は L3 が担保).
  useEffect(() => {
    const engineState = recordingStatus.engine.state
    if (engineState === 'completed') {
      recordingStatus.engine.reset()
      return
    }
    if (engineState === 'failed' && !recordingStatus.engine.hasPendingBlob) {
      recordingStatus.engine.reset()
    }
  }, [
    recordingStatus.engine.state,
    recordingStatus.engine.hasPendingBlob,
    recordingStatus.engine,
  ])

  // Pre/Post 切替判定:
  //  1. viewStatus='failed' && pendingBlob (= retry / DL 救済導線) なら Pre 固定
  //  2. それ以外は ratchet 済 recordingStatus.status='completed' か event.status が failure で Post
  //
  // 完了 SOAP の再生成中は back が event.status='processing' を返すが、 L3 ratchet で
  // recordingStatus.status='completed' が維持されるため Post 表示が継続する (= regen 中の
  // 一瞬の Pre view への view-flip を抑止).
  const isPostPhase =
    !(recordingStatus.status === 'failed' && recordingStatus.showRetryCta) &&
    event !== undefined &&
    (recordingStatus.status === 'completed' || isFailureStatus(event.status))

  // deep-link (= /yakureki/quick-record) 経由で history state.autoStart=true で
  // 着地した場合、 event が読み込めて Pre 画面が確定したら自動的に録音を開始する.
  // autoStart は event 確定時点で 1 回だけ消費する (= navigate replace で state を空に戻す).
  // 開始できない条件 (Post 画面 / engine 使用中) では発火せず捨てる: 消費せず残すと、
  // 後で engine が idle に戻った瞬間にユーザー操作なしで録音が自動開始されてしまう.
  // 判定はマスク済 recordingStatus.status を使う (= 表示中の録音開始 CTA と同じ基準.
  // 生 engine.state を見ると、 別患者の failed 保持中に CTA は出ているのに autoStart
  // だけ無言で不発、 という不整合になる).
  const navigate = useNavigate()

  // 開いていた薬歴が消えたら一覧を取り直して landing へ戻す (ADR-0039). 破棄でフリー録音の
  // event が削除されると以降この画面は 404 になる. usePollEvent が polling を止め (isGone)、
  // ここで着地させる = 戻り先の無い画面に留めない. 別端末の破棄や sweep の 404 も同じ扱い.
  // 一覧を先に取り直す理由 (landing との往復) は hook 側に.
  useGoneRedirect(isGone, yakurekiId)

  // フリー録音 (/soap/new) / まとめて録音からの遷移で運ばれた低速文字起こし hint.
  // engine.isSlowTranscription は遷移元 page の hook instance に閉じるため、
  // history state 経由で引き継いで MeetingBlock の低速バナーを継続表示する.
  // 本 page で新しい録音が始まったら (= engine が一度でも idle を離れたら) stale とみなして
  // 以後恒久的に無効化する (以降は本 page の engine 自身の flag が担う).
  // ※ terminal cleanup effect が completed 直後に engine.reset() で idle に戻すため、
  //   「今 idle か」ではなく「一度でも動いたか」を sticky に記憶する必要がある.
  const [slowHintInvalidated, setSlowHintInvalidated] = useState(false)
  if (!slowHintInvalidated && recordingStatus.engine.state !== 'idle') {
    setSlowHintInvalidated(true)
  }
  const routedSlowTranscription =
    !slowHintInvalidated &&
    (location.state as { slowTranscription?: boolean } | null)
      ?.slowTranscription === true
  // 低速 hint の 1 bit 版 (log/207): 遷移元の録音が「意図した batch」だったか. 文言分岐用.
  const routedIntendedBatch =
    routedSlowTranscription &&
    (location.state as { intendedBatch?: boolean } | null)?.intendedBatch ===
      true

  const autoStartFired = useRef(false)
  useEffect(() => {
    if (autoStartFired.current) return
    const state = location.state as { autoStart?: boolean } | null
    if (!state?.autoStart) return
    if (!yakurekiId) return
    if (!event) return
    autoStartFired.current = true
    navigate(location.pathname, { replace: true, state: {} })
    if (isPostPhase) return
    if (recordingStatus.status !== 'idle') return
    handleStartRecording()
  }, [
    location.state,
    location.pathname,
    yakurekiId,
    event,
    isPostPhase,
    recordingStatus.status,
    handleStartRecording,
    navigate,
  ])

  return (
    <Layout
      breadcrumb={
        homecare
          ? ['在宅', event?.label || '訪問']
          : ['薬歴', yakurekiId ?? '(未選択)']
      }
    >
      {homecare && yakurekiId && (
        <HomecareVisitBackLink yakurekiId={yakurekiId} />
      )}
      {/* 埋め込みの突合ガード。Pre/Post 両相を跨いで、不一致のときだけ赤帯が出る。 */}
      <PatientMismatchBanner patientName={event?.patient_name} />
      {yakurekiId ? (
        isPostPhase && event ? (
          // key で記録ごとに作り直す。表示中の view・開いていた dialog・書きかけの
          // 改善依頼など「この記録 1 件の UI 状態」を次の記録へ持ち越さないため
          // (詳細 cache があると id が変わっても再 mount されない)。
          // 兄弟と重ならないよう key には部品ごとの接頭辞を付ける (yakureki/sibling-key)。
          <PostRecordingView
            key={`post-${event.id}`}
            event={event}
            reloadEvent={async () => {}}
          />
        ) : (
          <>
            {event && (
              <Box sx={{ mb: isEmbedded ? 2 : 3 }}>
                <PatientInfoBar
                  event={event}
                  recordingStatus={recordingStatus}
                  onStartRecording={handleStartRecording}
                  hasCompanion={hasCompanion}
                  onCompanionToggle={setHasCompanionOverride}
                  // 在宅の訪問のページでは来局に切り替えさせない (訪問は患者のページから作る)
                  sessionType={
                    homecare ? undefined : event.session_type ?? 'walk_in'
                  }
                  onSessionTypeChange={
                    homecare ? undefined : handleSessionTypeChange
                  }
                  preset={preset}
                  onPresetChange={setPreset}
                  pharmacists={pharmacistList}
                  sessionPharmacistId={sessionPharmacistId}
                  onPharmacistChange={setSessionPharmacistId}
                  onSkipRecording={
                    canSkipRecording ? handleSkipRecording : undefined
                  }
                  isSkippingRecording={isSkipPending}
                  skipRecordingLabel={
                    hasMemo ? 'メモから SOAP を作成' : '録音せずに記載'
                  }
                  skipRecordingTooltip={
                    hasMemo
                      ? 'メモだけを根拠に SOAP を生成する (録音なし)'
                      : '録音せずに SOAP を記載する'
                  }
                />
              </Box>
            )}
            <Stack spacing={2}>
              <MeetingBlock
                memoFlushRef={memoFlushRef}
                yakurekiId={yakurekiId}
                recordingStatus={recordingStatus}
                slowTranscriptionHint={routedSlowTranscription}
                intendedBatchHint={routedIntendedBatch}
              />
              {event && event.patient_id && <GuidancePrepPane event={event} />}
              {/* あまり触らない患者単位の補助操作 (加算履歴/疑義照会/調剤鑑査) と遠隔服薬指導
                  はページ最下部に低 emphasis で置く. */}
              {event && event.patient_id && (
                <PatientActionMenu
                  yakurekiId={event.id}
                  patientName={event.patient_name || event.label || '(無名)'}
                />
              )}
            </Stack>
          </>
        )
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 6, textAlign: 'center' }}
        >
          左の「最近の薬歴」から薬歴を選択してください
        </Typography>
      )}
      {/* 遠隔服薬指導の行は Pre/Post の外に 1 つ。通話の音声が処方に入って SOAP ができると
          Pre → Post に切り替わるが、そのとき行 (開いている申し送りの窓・通話・送れなかった
          録音) を作り直さない。記録ごとの作り直し (key) はそのまま。上との間隔は行が持つ。
          key は上の PostRecordingView と同じ親の兄弟なので、接頭辞で分ける
          (同じ key が並ぶと患者を替えても前の画面が外れず、下へ積み上がる) */}
      {yakurekiId && event && (
        <RemoteGuidanceRow key={`remote-${event.id}`} event={event} />
      )}
    </Layout>
  )
}

// ActiveRecordingProvider は latest shell の Sidebar + MeetingBlock 録音中表示で使う.
import { ActiveRecordingProvider } from '@/apps/latest/shared/contexts/ActiveRecordingContext'

const LatestIndexPage: FC<IndexPageProps> = (props) => (
  <ActiveRecordingProvider>
    <IndexPage {...props} />
  </ActiveRecordingProvider>
)

export default LatestIndexPage
